import { AppTextField } from "components/common/inputs";
import { AppDialog } from "components/common/dialogs";
import { AppButton } from "components/common/buttons";
import React, { useState, useEffect, useCallback } from "react";
import {
  Alert,
  Paper,
  Box,
  Typography,
  Chip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { MaterialReactTable } from "material-react-table";
import { MRT_Localization_VI } from "material-react-table/locales/vi";
import { getToken } from "services/auth.service";

const ContractSelectionModal = ({
  open,
  onClose,
  onSelect,
  currentContractId,
}) => {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [totalCount, setTotalCount] = useState(0);
  const [globalFilter, setGlobalFilter] = useState("");
  const [sorting, setSorting] = useState([]);

  // Function to get status color (same as ProjectList.js)
  const getStatusColor = (status) => {
    const statusLower = status ? status.toLowerCase() : "";

    const colors = {
      "chưa bắt đầu": "#ffd700",
      "đang thực hiện": "#4caf50",
      "hoàn thành": "#2196f3",
      "tạm dừng": "#ff9800",
      "hủy bỏ": "#f44336",
      "not started": "#ffd700",
      "in progress": "#4caf50",
      completed: "#2196f3",
      "on hold": "#ff9800",
      cancelled: "#f44336",
    };

    return colors[statusLower] || "#9e9e9e";
  };

  const columns = [
    {
      accessorKey: "contractNumber",
      header: "Số hợp đồng",
      size: 180,
    },
    {
      accessorKey: "contractName",
      header: "Tên hợp đồng",
      size: 350,
      muiTableBodyCellProps: {
        sx: {
          whiteSpace: "normal",
          wordWrap: "break-word",
          lineHeight: 1.4,
          maxWidth: "350px",
        },
      },
    },
    {
      accessorKey: "status",
      header: "Trạng thái",
      size: 150,
      Cell: ({ cell }) => {
        const status = cell.getValue();
        return (
          <Chip
            label={status || "Chưa xác định"}
            size="small"
            sx={{
              backgroundColor: getStatusColor(status),
              color: "white",
              fontWeight: 500,
              fontSize: "11px",
              height: "24px",
              fontFamily: "Inter",
            }}
          />
        );
      },
    },
  ];

  const fetchContracts = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const token = await getToken();
      if (!token) {
        setError("Phiên đăng nhập đã hết hạn");
        return;
      }

      // Prepare request data similar to UserRoleManagement
      const requestData = {
        excludedContractIds: [], // No contracts to exclude for calendar
        pageNumber: pagination.pageIndex + 1, // MaterialReactTable uses 0-based indexing
        pageSize: pagination.pageSize,
        sortColumn: sorting.length > 0 ? sorting[0].id : "ContractNumber",
        sortDirection:
          sorting.length > 0 ? (sorting[0].desc ? "DESC" : "ASC") : "ASC",
      };

      // Add search term if provided
      if (globalFilter && globalFilter.trim()) {
        requestData.searchTerm = globalFilter.trim();
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/Contract/assigned-to-user`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestData),
        }
      );

      if (!response.ok) {
        throw new Error("Không thể tải danh sách hợp đồng");
      }

      const result = await response.json();


      // Handle response similar to UserRoleManagement
      if (result.success && result.data) {

        setContracts(result.data);
        setTotalCount(result.totalCount || 0);
      } else {
        console.error("API returned error:", result.message);
        setError(result.message || "Lỗi khi tải danh sách hợp đồng");
        setContracts([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error("Error fetching contracts:", error);
      setError("Lỗi khi tải danh sách hợp đồng");
      setContracts([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [pagination, sorting, globalFilter]);

  useEffect(() => {
    if (open) {
      fetchContracts();
    }
  }, [open, fetchContracts]);

  // Load contracts when pagination changes
  useEffect(() => {
    if (open) {
      fetchContracts();
    }
  }, [pagination, open, fetchContracts]);

  // Handle search term changes
  useEffect(() => {
    if (open && globalFilter !== "") {
      // Reset to first page when search changes
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }
  }, [globalFilter, open]);

  // Load contracts when sorting changes
  useEffect(() => {
    if (open) {
      fetchContracts();
    }
  }, [sorting, open, fetchContracts]);

  const handleClose = () => {
    setError("");
    setGlobalFilter("");
    setPagination({ pageIndex: 0, pageSize: 10 });
    setSorting([]);
    onClose();
  };

  const handleSelect = (contract) => {
    onSelect(contract);
    handleClose();
  };

  return (
    <AppDialog
      open={open}
      onClose={handleClose}
      title="Chọn hợp đồng"
      size="lg"
      actions={
        <>
          <AppButton
            variant="outlined"
            color="inherit"
            onClick={handleClose}
          >
            Hủy
          </AppButton>
        </>
      }
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 2 }}>
        <AppTextField
          placeholder="Tìm kiếm hợp đồng..."
          value={globalFilter}
          onChange={(value) => setGlobalFilter(value)}
          startIcon={<SearchIcon />}
          sx={{ width: { xs: "100%", sm: 300 } }}
        />
      </Box>

      <Paper sx={{ border: "1px solid #e0e0e0", boxShadow: "none" }}>
        <MaterialReactTable
          columns={columns}
          data={contracts}
          enableColumnActions={false}
          enableColumnFilters={false}
          enablePagination={true}
          enableSorting={true}
          enableBottomToolbar={true}
          enableTopToolbar={false}
          manualPagination={true}
          manualSorting={true}
          muiTableBodyRowProps={({ row }) => ({
            onClick: () => handleSelect(row.original),
            sx: {
              cursor: "pointer",
              backgroundColor:
                currentContractId === row.original.contractId
                  ? "#f5f5f5"
                  : "inherit",
              "&:hover": {
                backgroundColor: "#f0f0f0",
              },
            },
          })}
          muiTablePaperProps={{
            sx: {
              boxShadow: "none",
              border: "none",
            },
          }}
          muiTableProps={{
            sx: {
              fontFamily: "Inter",
            },
          }}
          muiTableHeadCellProps={{
            sx: {
              fontFamily: "Inter",
              fontWeight: 600,
              backgroundColor: "#f8f9fa",
            },
          }}
          muiTableBodyCellProps={{
            sx: {
              fontFamily: "Inter",
            },
          }}
          onPaginationChange={setPagination}
          onSortingChange={setSorting}
          rowCount={totalCount}
          state={{
            isLoading: loading,
            pagination,
            sorting,
          }}
          initialState={{
            density: "compact",
          }}
          muiCircularProgressProps={{
            color: "primary",
            thickness: 5,
            size: 30,
          }}
          localization={MRT_Localization_VI}
        />
      </Paper>

      {currentContractId && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 1, fontFamily: "Inter" }}
        >
          Hợp đồng hiện tại được đánh dấu màu xám
        </Typography>
      )}
    </AppDialog>
  );
};

export default ContractSelectionModal;
